/**
 * SeletorCenarios - Seletor de Minutas por Cenário
 *
 * CONTEXTO DE NEGÓCIO:
 * Exibe as minutas geradas pela IA (cada uma associada a um cenário/hipótese)
 * e permite ao usuário escolher qual deseja editar no editor de minutas.
 * Destaca visualmente a minuta recomendada.
 *
 * CONTRATO (inputs/outputs):
 * - Inputs:
 *   - processoId?: string — se fornecido, o seletor buscará o conjunto_minutas
 *     via GET /api/processos/{id}.
 *   - conjuntoMinutas?: ConjuntoMinutas — se fornecido diretamente, evita a busca.
 *   - idMinutaSelecionada?: string — id atualmente selecionado para marcação.
 * - Output:
 *   - onSelecionar(id: string): void — callback disparado ao selecionar uma minuta;
 *     também é disparado automaticamente na montagem com a recomendada (se existir)
 *     ou a primeira da lista.
 */

import { useEffect, useMemo, useState } from 'react';
import { BadgeCheck, ChevronDown, FileText, Star } from 'lucide-react';
import { obterProcesso } from '../../servicos/servicoApiProcessos';
import type { ConjuntoMinutas, MinutaGerada } from '../../tipos/tiposMinutas';

interface SeletorCenariosProps {
  processoId?: string;
  conjuntoMinutas?: ConjuntoMinutas;
  idMinutaSelecionada?: string;
  onSelecionar: (idMinuta: string) => void;
  className?: string;
}

export default function SeletorCenarios(props: SeletorCenariosProps): JSX.Element {
  const { processoId, conjuntoMinutas, idMinutaSelecionada, onSelecionar, className } = props;

  const [carregando, setCarregando] = useState<boolean>(false);
  const [erro, setErro] = useState<string>('');
  const [dados, setDados] = useState<ConjuntoMinutas | null>(conjuntoMinutas || null);
  const [aberto, setAberto] = useState<boolean>(false);

  // Buscar conjunto_minutas se não veio por props
  useEffect(() => {
    (async () => {
      if (dados || !processoId) return;
      try {
        setCarregando(true);
        setErro('');
        const resp = await obterProcesso(processoId);
        const processo = resp.data;
        const metadados = (processo?.metadados as Record<string, unknown>) || {};
        const resultado = (metadados.resultado_analise as Record<string, unknown>) || {};
        const conjunto = (resultado.conjunto_minutas as ConjuntoMinutas) || null;
        setDados(conjunto);
      } catch {
        setErro('Não foi possível carregar as minutas deste processo.');
      } finally {
        setCarregando(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processoId]);

  // Garantir emissão inicial de seleção (recomendada -> primeira)
  useEffect(() => {
    if (!dados || !dados.minutas || dados.minutas.length === 0) return;
    const atual = idMinutaSelecionada || dados.minuta_recomendada_id || dados.minutas[0]?.id_minuta;
    if (atual) onSelecionar(atual);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(dados && dados.minutas && dados.minutas.length > 0)]);

  const selecionada: MinutaGerada | undefined = useMemo(() => {
    const lista = dados?.minutas || [];
    const alvoId = idMinutaSelecionada || dados?.minuta_recomendada_id;
    return lista.find(m => m.id_minuta === alvoId) || lista[0];
  }, [dados, idMinutaSelecionada]);

  const listaOrdenada: MinutaGerada[] = useMemo(() => {
    const l = [...(dados?.minutas || [])];
    // Tentar ordenar por probabilidade (desc) mantendo a recomendada no topo
    l.sort((a, b) => (b.probabilidade_cenario || 0) - (a.probabilidade_cenario || 0));
    if (dados?.minuta_recomendada_id) {
      const idx = l.findIndex(m => m.id_minuta === dados.minuta_recomendada_id);
      if (idx > 0) {
        const [rec] = l.splice(idx, 1);
        l.unshift(rec);
      }
    }
    return l;
  }, [dados]);

  // Empty state
  if (!carregando && (!dados || !dados.minutas || dados.minutas.length === 0)) {
    return (
      <div className={"w-full border border-amber-200 bg-amber-50 text-amber-900 rounded-lg p-4 " + (className || '')}>
        <div className="flex items-start gap-3">
          <FileText className="mt-0.5 size-5 text-amber-600" />
          <div>
            <div className="font-semibold">Nenhuma minuta disponível para este processo</div>
            <div className="mt-1 text-sm">
              Tente reprocessar a análise ou verifique os logs do backend. Caso o problema persista, confirme se os agentes geradores de minutas estão ativos.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={"relative " + (className || '')}>
      {/* Cabeçalho do seletor (dropdown compacto com destaque da recomendada) */}
      <button
        type="button"
        onClick={() => setAberto(v => !v)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 shadow-sm hover:bg-gray-50"
        aria-haspopup="listbox"
        aria-expanded={aberto}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="size-2 shrink-0 rounded-full bg-primary-500" />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-800">
              {selecionada?.titulo || 'Selecionar minuta'}
            </div>
            <div className="truncate text-xs text-gray-500">
              {selecionada?.cenario_vinculado || 'Cenário não informado'}
              {typeof selecionada?.probabilidade_cenario === 'number' && (
                <span className="ml-2 inline-flex items-center gap-1 rounded border border-primary-100 bg-primary-50 px-1.5 py-0.5 text-[11px] text-primary-700">
                  <BadgeCheck className="size-3" /> {selecionada?.probabilidade_cenario}%
                </span>
              )}
              {dados?.minuta_recomendada_id === selecionada?.id_minuta && (
                <span className="ml-2 inline-flex items-center gap-1 rounded border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">
                  <Star className="size-3" /> Recomendada
                </span>
              )}
            </div>
          </div>
        </div>
        <ChevronDown className={`size-4 text-gray-500 transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>

      {/* Lista de opções */}
      {aberto && (
        <ul
          role="listbox"
          className="absolute z-10 mt-2 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {carregando && (
            <li className="px-4 py-2 text-sm text-gray-500">Carregando minutas...</li>
          )}
          {erro && (
            <li className="px-4 py-2 text-sm text-red-600">{erro}</li>
          )}
          {listaOrdenada.map((m) => {
            const ativa = (idMinutaSelecionada || dados?.minuta_recomendada_id) === m.id_minuta;
            return (
              <li
                key={m.id_minuta}
                role="option"
                aria-selected={ativa}
                onClick={() => {
                  onSelecionar(m.id_minuta);
                  setAberto(false);
                }}
                className={`cursor-pointer border-b px-4 py-3 last:border-b-0 hover:bg-gray-50 ${ativa ? 'bg-primary-50' : ''}`}
              >
                <div className="flex items-center gap-2">
                  <div className={`h-6 w-1.5 rounded ${ativa ? 'bg-primary-500' : 'bg-gray-200'}`} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-800">{m.titulo || 'Minuta'}</div>
                    <div className="truncate text-xs text-gray-500">
                      {m.cenario_vinculado || 'Cenário não informado'}
                      {typeof m.probabilidade_cenario === 'number' && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded border border-primary-100 bg-primary-50 px-1.5 py-0.5 text-[11px] text-primary-700">
                          <BadgeCheck className="size-3" /> {m.probabilidade_cenario}%
                        </span>
                      )}
                      {dados?.minuta_recomendada_id === m.id_minuta && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700">
                          <Star className="size-3" /> Recomendada
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
